import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Copy, Trash2, Play, AlertCircle, CheckCircle, BookOpen } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface RegexMatch {
  match: string;
  index: number;
  groups: string[];
}

interface RegexFlags {
  global: boolean;
  ignoreCase: boolean;
  multiline: boolean;
  dotAll: boolean;
  unicode: boolean;
  sticky: boolean;
}

const RegexTester: React.FC = () => {
  const [pattern, setPattern] = useState('');
  const [testString, setTestString] = useState('');
  const [flags, setFlags] = useState<RegexFlags>({
    global: true,
    ignoreCase: false,
    multiline: false,
    dotAll: false,
    unicode: false,
    sticky: false
  });
  const [matches, setMatches] = useState<RegexMatch[]>([]);
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');
  const [highlightedText, setHighlightedText] = useState('');

  useSEO({
    title: 'Regex Tester - Test Regular Expressions Online',
    description: 'Free online regex tester. Test and debug regular expressions with real-time matching, syntax highlighting, and detailed match information.',
    keywords: 'regex tester, regular expression tester, regex validator, regex debugger, pattern matching',
    canonical: 'https://snap-tools.online/dev/regex-tester'
  });

  const getFlagString = (): string => {
    let flagStr = '';
    if (flags.global) flagStr += 'g';
    if (flags.ignoreCase) flagStr += 'i';
    if (flags.multiline) flagStr += 'm';
    if (flags.dotAll) flagStr += 's';
    if (flags.unicode) flagStr += 'u';
    if (flags.sticky) flagStr += 'y';
    return flagStr;
  };

  const testRegex = () => {
    if (!pattern) {
      setMatches([]);
      setIsValid(true);
      setError('');
      setHighlightedText(testString);
      return;
    }

    try {
      const regex = new RegExp(pattern, getFlagString());
      const foundMatches: RegexMatch[] = [];
      
      if (flags.global) {
        let match;
        while ((match = regex.exec(testString)) !== null) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1)
          });
          
          // Prevent infinite loop for zero-length matches
          if (match.index === regex.lastIndex) {
            regex.lastIndex++;
          }
        }
      } else {
        const match = regex.exec(testString);
        if (match) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1)
          });
        }
      }

      setMatches(foundMatches);
      setIsValid(true);
      setError('');
      
      // Create highlighted text
      if (foundMatches.length > 0) {
        let highlighted = testString;
        let offset = 0;
        
        foundMatches.forEach((match, index) => {
          const startTag = `<mark class="bg-yellow-200 text-yellow-900 font-medium" data-match="${index}">`;
          const endTag = '</mark>';
          const start = match.index + offset;
          const end = start + match.match.length;
          
          highlighted = highlighted.slice(0, start) + startTag + highlighted.slice(start, end) + endTag + highlighted.slice(end);
          offset += startTag.length + endTag.length;
        });
        
        setHighlightedText(highlighted);
      } else {
        setHighlightedText(testString);
      }
    } catch (e) {
      setIsValid(false);
      setError(e instanceof Error ? e.message : 'Invalid regular expression');
      setMatches([]);
      setHighlightedText(testString);
    }
  };

  useEffect(() => {
    testRegex();
  }, [pattern, testString, flags]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    });
  };

  const clearInputs = () => {
    setPattern('');
    setTestString('');
    setMatches([]);
    setError('');
    setHighlightedText('');
    toast.success('All fields cleared');
  };

  const loadSample = () => {
    setPattern('\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b');
    setTestString('Contact us at support@example.com or admin@test.org for help. Invalid: notanemail');
    setFlags({ ...flags, global: true, ignoreCase: true });
    toast.success('Email validation sample loaded');
  };

  const commonPatterns = [
    {
      name: 'Email',
      pattern: '\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b',
      description: 'Matches email addresses'
    },
    {
      name: 'Phone (US)',
      pattern: '\\(\\d{3}\\)\\s*\\d{3}-\\d{4}|\\d{3}-\\d{3}-\\d{4}',
      description: 'Matches US phone numbers'
    },
    {
      name: 'URL',
      pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)',
      description: 'Matches HTTP/HTTPS URLs'
    },
    {
      name: 'IP Address',
      pattern: '\\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\b',
      description: 'Matches IPv4 addresses'
    },
    {
      name: 'Date (MM/DD/YYYY)',
      pattern: '\\b(0?[1-9]|1[0-2])\\/(0?[1-9]|[12][0-9]|3[01])\\/(19|20)\\d{2}\\b',
      description: 'Matches MM/DD/YYYY format'
    },
    {
      name: 'Hex Color',
      pattern: '#[a-fA-F0-9]{6}|#[a-fA-F0-9]{3}\\b',
      description: 'Matches hex color codes'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Target className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Regex Tester & Debugger
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Test and debug regular expressions with real-time matching, syntax highlighting, and detailed match 
            information. Perfect for pattern validation and regex development.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Input Section */}
          <div className="lg:col-span-2">
            {/* Pattern Input */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8 mb-6"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Regular Expression Pattern
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={loadSample}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Sample
                  </button>
                  <button
                    onClick={clearInputs}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Clear all"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>

              <div className="relative">
                <div className="flex items-center mb-2">
                  <span className="text-gray-500 font-mono text-sm mr-1">/</span>
                  <input
                    type="text"
                    value={pattern}
                    onChange={(e) => setPattern(e.target.value)}
                    placeholder="Enter your regex pattern here..."
                    className={`flex-1 p-3 border-2 rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#8438ff] transition-colors ${
                      isValid ? 'border-gray-200 focus:border-[#8438ff]' : 'border-red-300 focus:border-red-500'
                    }`}
                  />
                  <span className="text-gray-500 font-mono text-sm ml-1">/{getFlagString()}</span>
                  <div className="flex items-center ml-2">
                    {isValid ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(flags).map(([flag, value]) => (
                    <label key={flag} className="flex items-center space-x-1 text-xs">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setFlags({ ...flags, [flag]: e.target.checked })}
                        className="rounded border-gray-300 text-[#8438ff] focus:ring-[#8438ff]"
                      />
                      <span className="capitalize">{flag.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </label>
                  ))}
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                      <span className="text-red-700 text-sm">{error}</span>
                    </div>
                  </div>
                )}

                {/* Pattern Info */}
                <div className="text-xs text-gray-600">
                  <p>Flags: {getFlagString() || 'none'} | Pattern length: {pattern.length} characters</p>
                </div>
              </div>
            </motion.div>

            {/* Test String Input */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8 mb-6"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Test String
                </h3>
                <button
                  onClick={() => copyToClipboard(testString, 'Test string')}
                  className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                  title="Copy test string"
                >
                  <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
              </div>

              <textarea
                value={testString}
                onChange={(e) => setTestString(e.target.value)}
                placeholder="Enter your test string here..."
                className="w-full h-40 sm:h-48 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors text-sm"
              />

              <div className="mt-3 text-xs text-gray-600">
                <p>String length: {testString.length} characters | Lines: {testString.split('\n').length}</p>
              </div>
            </motion.div>

            {/* Results Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Results ({matches.length} match{matches.length !== 1 ? 'es' : ''})
                </h3>
                <div className="flex items-center space-x-2">
                  <Play className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">Live Testing</span>
                </div>
              </div>

              {/* Highlighted Text */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Highlighted Matches</h4>
                <div 
                  className="p-3 sm:p-4 bg-gray-50 rounded-lg border min-h-[100px] font-mono text-sm whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: highlightedText || 'No text to display' }}
                />
              </div>

              {/* Match Details */}
              {matches.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Match Details</h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {matches.map((match, index) => (
                      <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-yellow-800">Match {index + 1}</span>
                          <button
                            onClick={() => copyToClipboard(match.match, `Match ${index + 1}`)}
                            className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-200 rounded transition-colors"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="text-xs text-yellow-700 space-y-1">
                          <p><strong>Text:</strong> "{match.match}"</p>
                          <p><strong>Position:</strong> {match.index} - {match.index + match.match.length - 1}</p>
                          {match.groups.length > 0 && (
                            <p><strong>Groups:</strong> {match.groups.map((group, i) => `$${i + 1}: "${group}"`).join(', ')}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {matches.length === 0 && pattern && testString && isValid && (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No matches found</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Common Patterns */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8 mb-6"
            >
              <div className="flex items-center mb-4">
                <BookOpen className="h-5 w-5 text-[#8438ff] mr-2" />
                <h3 className="text-lg font-bold text-gray-900">Common Patterns</h3>
              </div>

              <div className="space-y-3">
                {commonPatterns.map((item, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <button
                      onClick={() => {
                        setPattern(item.pattern);
                        toast.success(`${item.name} pattern loaded`);
                      }}
                      className="w-full text-left"
                    >
                      <div className="font-medium text-sm text-gray-900 mb-1">{item.name}</div>
                      <div className="text-xs text-gray-600 mb-2">{item.description}</div>
                      <div className="text-xs font-mono bg-gray-100 p-1 rounded break-all">{item.pattern}</div>
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Quick Reference */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Reference</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Character Classes</h4>
                  <div className="text-xs text-gray-600 space-y-1 font-mono">
                    <div><span className="text-[#8438ff]">.</span> - Any character</div>
                    <div><span className="text-[#8438ff]">\d</span> - Digit (0-9)</div>
                    <div><span className="text-[#8438ff]">\w</span> - Word character</div>
                    <div><span className="text-[#8438ff]">\s</span> - Whitespace</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Quantifiers</h4>
                  <div className="text-xs text-gray-600 space-y-1 font-mono">
                    <div><span className="text-[#8438ff]">*</span> - 0 or more</div>
                    <div><span className="text-[#8438ff]">+</span> - 1 or more</div>
                    <div><span className="text-[#8438ff]">?</span> - 0 or 1</div>
                    <div><span className="text-[#8438ff]">{`{n,m}`}</span> - Between n and m</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Anchors</h4>
                  <div className="text-xs text-gray-600 space-y-1 font-mono">
                    <div><span className="text-[#8438ff]">^</span> - Start of string</div>
                    <div><span className="text-[#8438ff]">$</span> - End of string</div>
                    <div><span className="text-[#8438ff]">\b</span> - Word boundary</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default RegexTester;
